import { validateCoupon } from 'v6/api/signup';
import * as Collapsible from '@radix-ui/react-collapsible';
import { Input, Button } from 'Components';
import { CaretVerticalThin, CrossIcon, TagIcon } from 'assets/icons';
import { useState } from 'react';
import styled from 'styled-components';
import { Alert } from 'v6/Components/Alert';
import { openSupportChat } from 'utils/open.support.chat';

export const CouponForm = ({
  coupon,
  couponAdded,
  chargebeeError,
  setChargebeeError,
  setCoupon,
  plan,
  currency,
}: {
  coupon: string;
  couponAdded: boolean;
  chargebeeError: string;
  setCoupon: (coupon: string) => void;
  setChargebeeError: (val: string) => void;
  plan: string;
  currency: string;
}) => {
  const [couponUi, setCouponUi] = useState('');
  const [couponError, setCouponError] = useState<boolean>(false);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  const handleAddCoupon = async () => {
    setCouponError(false);
    setChargebeeError('');
    setIsValidatingCoupon(true);
    const { valid } = await validateCoupon(couponUi, plan, currency);
    setIsValidatingCoupon(false);

    if (valid) {
      setCoupon(couponUi);
    } else {
      setCouponError(true);
      setCoupon('');
    }
  };

  const onOpenContact = () => {
    openSupportChat();
  };

  const onRemoveCoupon = () => {
    setCoupon('');
    setCouponUi('');
  };

  return (
    <CouponFormWrapper>
      <Collapsible.Root>
        <Collapsible.Trigger asChild>
          <Trigger data-testid="couponTrigger">
            Do you have a coupon code?
            <CaretVerticalThinStyled />
          </Trigger>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <Content>
            <StyledInput
              data-testid="couponInput"
              type="text"
              name="coupon"
              placeholder="Coupon code"
              className="data-hj-allow"
              value={couponUi}
              onChange={e => {
                setCouponUi(e.target.value);
              }}
            />
            <StyledButton
              disabled={isValidatingCoupon || !couponUi}
              data-testid="couponAddButton"
              onClick={handleAddCoupon}
            >
              Add
            </StyledButton>
          </Content>

          {couponAdded && (
            <CouponWrap>
              <TagIcon />
              <span>{coupon}</span>
              <RemoveIconWrap onClick={onRemoveCoupon}>
                <CrossIcon />
              </RemoveIconWrap>
            </CouponWrap>
          )}

          <MessageWrap>
            {couponAdded && !chargebeeError && !couponError && (
              <Alert type="success">{`
            Coupon code ${coupon} has been successfully applied. Please check your updated price in the
            Order Summary.
          `}</Alert>
            )}
            {(couponError || chargebeeError) && (
              <Alert type="error">
                {couponError ? (
                  <>
                    Invalid coupon code or you have entered a referral code from another user, which
                    is automatically applied after signing up.{' '}
                    <button onClick={onOpenContact}>Contact support</button> if you're unsure about
                    your coupon code.
                  </>
                ) : (
                  chargebeeError
                )}
              </Alert>
            )}
          </MessageWrap>
        </Collapsible.Content>
      </Collapsible.Root>
    </CouponFormWrapper>
  );
};

const CouponFormWrapper = styled.div`
  margin: 16px auto;
`;

const Trigger = styled.div`
  width: 100%;
  text-align: center;
  cursor: pointer;
  color: var(--Text-T02, #454545);
  /* Web App/Paragraph 2 Regular */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
`;

const CaretVerticalThinStyled = styled(CaretVerticalThin)`
  display: inline-block;
  width: 10px;
  margin-left: 5px;
  margin-bottom: 3px;
  color: #454544;
`;

const Content = styled.div`
  display: flex;
  margin-top: 18px;
  gap: 7px;
`;

const StyledInput = styled(Input)`
  width: auto;
  flex-grow: 1;
`;

const StyledButton = styled(Button)`
  width: 120px;
  flex-shrink: 0;
`;

const MessageWrap = styled.div`
  margin-top: 14px;
`;

const CouponWrap = styled.div`
  margin-top: 16px;
  border-radius: 4px;
  background: var(--Green-G25, #f5fffa);
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  color: var(--Green-G500, #10b981);
  text-align: center;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */

  span {
    margin: 0 4px;
  }

  svg {
  }
`;

const RemoveIconWrap = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  svg {
    width: 10px;
  }
`;
